import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as LucideIcons from 'lucide-react';
import { collection, query, where, orderBy, limit, startAfter, getDocs, doc, documentId, getDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useInView } from 'react-intersection-observer';
import Lottie from 'lottie-react';
import { Helmet } from 'react-helmet-async';

import { useTranslation } from 'react-i18next';
import { useAppContext } from '../AppContext';
import useDebounce from '../hooks/useDebounce';
import LoadingSpinner from './LoadingSpinner';
import AIGeneratedContentModal from './AIGeneratedContentModal';
import MediaCard from './MediaCard'; // Import the new MediaCard component
import NexusCard from './NexusCard';
import ProfileStatModal from './ProfileStatModal';
import ProgressBar from './ProgressBar';
import MutualConnections from './MutualConnections';
import AuraComparisonModal from './AuraComparisonModal';
import BadgeShowcase from './BadgeShowcase';
import PinnedBadges from './PinnedBadges';
import BadgeIconStrip from './BadgeIconStrip';
import BadgeInfoModal from '././BadgeInfoModal';
import './UserProfile.css';
import Masonry from 'react-masonry-css';
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import CreateContentModal from './CreateContentModal';
import JourneyTab from './JourneyTab';
import InteractionUsersModal from './InteractionUsersModal';
import HoverTooltip from './HoverTooltip';
import WhisperCard from './WhisperCard';
import CommentModal from './CommentModal';
import InteractiveConstellation from './InteractiveConstellation';
import ThemePurchaseModal from './ThemePurchaseModal';
import ShowdownModal from './ShowdownModal';
import EditProfileModal from './EditProfileModal';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const getProfileThemes = (t) => ({
    default: { name: t('profileCustomization.themes.default'), previewClass: 'bg-gray-800' },
    starlight: { name: t('profileCustomization.themes.starlight'), previewClass: 'bg-gradient-to-br from-indigo-900 to-purple-900', requiredBadge: 'elder_star' },
    sunrise: { name: t('profileCustomization.themes.sunrise'), previewClass: 'bg-gradient-to-br from-amber-500 to-red-700', requiredBadge: 'echo_deity' },
    verdant: { name: t('profileCustomization.themes.verdant'), previewClass: 'bg-gradient-to-br from-green-700 to-teal-900', requiredBadge: 'community_pillar' },
    abyssal: { name: t('profileCustomization.themes.abyssal'), previewClass: 'bg-gradient-to-br from-cyan-900 to-blue-900', requiredBadge: 'harmony_sage' },
    celestial: { name: t('profileCustomization.themes.celestial'), previewClass: 'bg-gradient-to-br from-yellow-100 to-blue-200', requiredBadge: 'elder_star' },
    glitch: { name: t('profileCustomization.themes.glitch'), previewClass: 'bg-black', requiredBadge: 'bug_hunter' },
    holographic: { name: t('profileCustomization.themes.holographic'), previewClass: 'bg-gradient-to-br from-purple-500 to-cyan-500', requiredBadge: 'echo_magnate' },
    neonnoir: { name: t('profileCustomization.themes.neonnoir'), previewClass: 'bg-gradient-to-br from-blue-900 to-purple-900', requiredBadge: 'archivist' },
    synthwave: { name: t('profileCustomization.themes.synthwave'), previewClass: 'bg-gradient-to-br from-pink-500 to-purple-600', requiredBadge: 'trendsetter' },
    moltencore: { name: t('profileCustomization.themes.moltencore'), previewClass: 'bg-gradient-to-br from-red-800 to-yellow-600', requiredBadge: 'prolific_commenter' },
    aperture: { name: t('profileCustomization.themes.aperture'), previewClass: 'bg-gray-200', requiredBadge: 'networker' },
    foundersorb: { name: t('profileCustomization.themes.foundersorb'), previewClass: 'bg-gradient-to-br from-gray-700 via-gray-900 to-black', requiresFounder: true },
    presignup: { name: t('profileCustomization.themes.presignup'), previewClass: 'bg-gradient-to-br from-rose-900 to-indigo-900', requiresFounder: true },
    
    cyberpunk: { name: "Cyberpunk City", previewClass: 'bg-gradient-to-r from-pink-600 via-purple-700 to-blue-800', requiredWhispers: 50, unlockHint: "Post 50 Whispers" },
    autumn: { name: "Autumn Breeze", previewClass: 'bg-gradient-to-br from-orange-700 to-yellow-900', requiredDays: 30, unlockHint: "Member for 30 Days" },
    oceanic: { name: "Deep Ocean", previewClass: 'bg-gradient-to-b from-teal-900 to-blue-950', requiredReputation: 100, unlockHint: "Reach 100 Reputation" },
    monochrome: { name: "Noir", previewClass: 'bg-gradient-to-t from-black to-gray-800', requiredConnections: 10, unlockHint: "Make 10 Connections" },
    sakura: { name: "Sakura Petals", previewClass: 'bg-gradient-to-tr from-pink-200 to-rose-300', isPublic: true },

    royal_gold: { name: "Royal Gold", previewClass: 'bg-gradient-to-r from-yellow-600 via-yellow-400 to-yellow-700', isPaid: true, price: 4.99, itemId: 'theme-royal-gold' },
    crimson_vampire: { name: "Crimson Night", previewClass: 'bg-gradient-to-b from-red-950 to-black', isPaid: true, price: 4.99, itemId: 'theme-crimson-vampire' },
    diamond: { name: "Diamond Lustre", previewClass: 'bg-gradient-to-tr from-gray-300 via-white to-gray-400', isPaid: true, price: 9.99, itemId: 'theme-diamond' },
    rainbow_prism: { name: "Prism", previewClass: 'bg-gradient-to-r from-red-500 via-green-500 to-blue-500 animate-gradient', isPaid: true, price: 9.99, itemId: 'theme-rainbow-prism' },
    void_walker: { name: "Void Walker", previewClass: 'bg-black border-2 border-purple-500 shadow-lg shadow-purple-500/50', isPaid: true, price: 14.99, itemId: 'theme-void-walker' },
    harmony_pro: { name: "Harmony Pro", previewClass: 'bg-gradient-to-br from-purple-900 to-yellow-500', isPaid: true, price: 19.99, itemId: 'theme-harmony-pro' },
});


const SoulBlueprintDisplay = ({ blueprint, LucideIcons, t, onClose }) => {
    const data = {
        labels: [
            'Fire (Passion)',
            'Water (Emotion)',
            'Earth (Stability)',
            'Air (Intellect)'
        ],
        datasets: [
            {
                label: 'Elemental Resonance',
                data: [
                    blueprint.elements.fire,
                    blueprint.elements.water,
                    blueprint.elements.earth,
                    blueprint.elements.air
                ],
                backgroundColor: 'rgba(139, 92, 246, 0.2)',
                borderColor: 'rgba(139, 92, 246, 1)',
                borderWidth: 2,
                pointBackgroundColor: 'rgba(139, 92, 246, 1)',
                pointBorderColor: '#fff',
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: 'rgba(139, 92, 246, 1)',
            },
        ],
    };

    const options = {
        scales: {
            r: {
                angleLines: { color: 'rgba(255, 255, 255, 0.1)' },
                grid: { color: 'rgba(255, 255, 255, 0.1)' },
                pointLabels: { color: '#e5e7eb', font: { size: 12 } },
                ticks: { display: false, backdropColor: 'transparent' },
                suggestedMin: 0,
                suggestedMax: 100,
            },
        },
        plugins: {
            legend: { display: false },
        },
        maintainAspectRatio: false,
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
            <div className="bg-gray-900 border border-purple-500/30 rounded-xl shadow-2xl max-w-2xl w-full p-6 relative overflow-y-auto max-h-[90vh] custom-scrollbar">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white"><LucideIcons.X size={24} /></button>
                
                <div className="text-center mb-8">
                    <LucideIcons.Fingerprint size={48} className="mx-auto text-purple-400 mb-2" />
                    <h3 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-300 via-pink-300 to-blue-300 font-sans">Soul Blueprint</h3>
                    <p className="text-sm text-gray-400">The architecture of your digital spirit.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Radar Chart */}
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10 flex flex-col items-center justify-center h-64">
                        <h4 className="text-sm font-bold text-gray-300 uppercase tracking-widest mb-4">Elemental Resonance</h4>
                        <div className="w-full h-full">
                            <Radar data={data} options={options} />
                        </div>
                    </div>

                    {/* Spirit Symbol & Values */}
                    <div className="space-y-6">
                        <div className="bg-gradient-to-br from-indigo-900/50 to-purple-900/50 rounded-xl p-6 border border-white/10 text-center">
                            <h4 className="text-xs text-indigo-300 uppercase tracking-widest mb-2">Spirit Symbol</h4>
                            <div className="text-4xl font-bold text-white mb-2">{blueprint.spiritSymbol}</div>
                            <p className="text-sm text-gray-300 italic">"{blueprint.symbolDescription}"</p>
                        </div>

                        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                            <h4 className="text-xs text-green-400 uppercase tracking-widest mb-4">Core Pillars</h4>
                            <div className="flex flex-wrap gap-2 justify-center">
                                {blueprint.coreValues.map((val, i) => (
                                    <span key={i} className="px-3 py-1 bg-green-500/20 text-green-300 border border-green-500/30 rounded-full text-sm font-medium">
                                        {val}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const getReputationLevels = (t) => [
    { level: 1, threshold: 0, name: t('userProfile.reputation.newcomer') },
    { level: 2, threshold: 100, name: t('userProfile.reputation.respectedVoice') },
    { level: 3, threshold: 500, name: t('userProfile.reputation.communityPillar') },
    { level: 4, threshold: 1000, name: t('userProfile.reputation.nexusElder') },
    { level: 5, threshold: 5000, name: t('userProfile.reputation.harmonySage') },
];

const getInfluenceLevels = (t) => [
    { level: 1, threshold: 0, name: t('userProfile.influence.spark') },
    { level: 2, threshold: 1000, name: t('userProfile.influence.spotlighter') },
    { level: 3, threshold: 5000, name: t('userProfile.influence.influencer') },
    { level: 4, threshold: 10000, name: t('userProfile.influence.tastemaker') },
    { level: 5, threshold: 50000, name: t('userProfile.influence.moverAndShaker') },
];

const getNextLevel = (score, levels) => {
    const currentLevel = levels.slice().reverse().find(l => score >= l.threshold);
    const nextLevel = levels.find(l => l.threshold > (currentLevel?.threshold || 0));
    return { currentLevel, nextLevel };
};

const PersonalitySnapshotDisplay = ({ snapshot, LucideIcons, t }) => {
    const traits = [
        { name: t('userProfile.personality.openness'), score: snapshot.openness, description: t('userProfile.personality.opennessDescription'), icon: LucideIcons.BrainCircuit },
        { name: t('userProfile.personality.conscientiousness'), score: snapshot.conscientiousness, description: t('userProfile.personality.conscientiousnessDescription'), icon: LucideIcons.ListChecks },
        { name: t('userProfile.personality.extraversion'), score: snapshot.extraversion, description: t('userProfile.personality.extraversionDescription'), icon: LucideIcons.Sun },
        { name: t('userProfile.personality.agreeableness'), score: snapshot.agreeableness, description: t('userProfile.personality.agreeablenessDescription'), icon: LucideIcons.HeartHandshake },
        { name: t('userProfile.personality.neuroticism'), score: snapshot.neuroticism, description: t('userProfile.personality.neuroticismDescription'), icon: LucideIcons.CloudRain },
    ];
    const TraitBar = ({ trait }) => {
        const Icon = trait.icon;
        return (
            <div>
                <div className="flex justify-between items-center mb-1">
                    <div className="flex items-center gap-2">
                        <Icon size={16} className="text-blue-300" />
                        <span className="font-bold text-white">{trait.name}</span>
                    </div>
                    <span className="text-sm font-mono text-gray-300">{trait.score}%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2.5"><div className="bg-blue-500 h-2.5 rounded-full" style={{ width: `${trait.score}%` }}></div></div>
                <p className="text-xs text-gray-400 mt-1">{trait.description}</p>
            </div>
        );
    };
    return (
        <div className="mt-6 p-4 bg-gray-800/50 rounded-lg border border-blue-500/20 animate-fadeIn">
            <h3 className="text-xl font-bold text-blue-200 font-sans mb-3">{t('userProfile.personality.title')}</h3>
            <blockquote className="border-l-4 border-blue-400 pl-4 text-gray-300 italic mb-4">"{snapshot.summary}"</blockquote>
            <div className="space-y-4">{traits.map(trait => <TraitBar key={trait.name} trait={trait} />)}</div>
        </div>
    );
};

const ConnectionCompassDisplay = ({ snapshot, LucideIcons, t }) => {
    const scoreColor = snapshot.compatibilityScore > 70 ? 'text-green-400' : snapshot.compatibilityScore > 40 ? 'text-yellow-400' : 'text-red-400';
    return (
        <div className="mt-6 p-4 bg-gray-800/50 rounded-lg border border-purple-500/20 animate-fadeIn">
            <h3 className="text-xl font-bold text-purple-200 font-sans mb-3">{t('userProfile.connectionCompass.title')}</h3>
            <div className="text-center mb-4">
                <p className="text-sm text-gray-400">{t('userProfile.connectionCompass.compatibilityScore')}</p>
                <p className={`text-6xl font-bold ${scoreColor}`}>{snapshot.compatibilityScore}</p>
                <div className="w-full bg-gray-700 rounded-full h-2.5 mt-2"><div className="bg-purple-500 h-2.5 rounded-full" style={{ width: `${snapshot.compatibilityScore}%` }}></div></div>
            </div>
            <blockquote className="border-l-4 border-purple-400 pl-4 text-gray-300 italic mb-4">"{snapshot.summary}"</blockquote>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <h4 className="font-bold text-green-400 mb-2 flex items-center gap-2"><LucideIcons.Zap size={16} /> {t('userProfile.connectionCompass.potentialSyncs')}</h4>
                    <ul className="list-disc list-inside space-y-1 text-sm text-gray-200">{snapshot.syncs.map((sync, i) => <li key={i}>{sync}</li>)}</ul>
                </div>
                <div>
                    <h4 className="font-bold text-red-400 mb-2 flex items-center gap-2"><LucideIcons.ShieldAlert size={16} /> {t('userProfile.connectionCompass.potentialDissonances')}</h4>
                    <ul className="list-disc list-inside space-y-1 text-sm text-gray-200">{snapshot.dissonances.map((dis, i) => <li key={i}>{dis}</li>)}</ul>
                </div>
            </div>
        </div>
    );
};

const BioEnhancerModal = ({ suggestions, onSelect, onClose, LucideIcons, t }) => (
    <div className="modal-overlay">
        <div className="bg-gray-900/80 backdrop-blur-md p-6 rounded-lg shadow-lg max-w-md w-full text-white relative border border-teal-500/50">
            <h3 className="text-xl font-bold mb-4 text-teal-300 font-sans">{t('userProfile.bioEnhancer.title')}</h3>
            <p className="text-gray-300 mb-6">{t('userProfile.bioEnhancer.description')}</p>
            <div className="space-y-3">
                {suggestions.map((bio, index) => (
                    <button key={index} onClick={() => onSelect(bio)} className="w-full text-left p-3 bg-gray-800/60 rounded-lg border border-gray-700 hover:bg-teal-500/20 hover:border-teal-500 transition-all">
                        <p className="italic text-gray-200">"{bio}"</p>
                    </button>
                ))}
            </div>
            <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full bg-gray-700/50 hover:bg-gray-600/50 transition duration-300" aria-label={t('userProfile.bioEnhancer.close')}><LucideIcons.X size={20} /></button>
        </div>
    </div>
);

const ThematicCloudDisplay = ({ cloud, LucideIcons, onThemeClick, t }) => {
    if (!cloud || !cloud.themes || cloud.themes.length === 0) {
        return (
            <div className="text-center text-gray-300 p-8 bg-gray-800/50 rounded-lg">
                <LucideIcons.CloudOff size={48} className="mx-auto mb-4 text-sky-400" />
                <h3 className="text-xl font-bold">{t('userProfile.thematicCloud.noData')}</h3>
                <p className="mt-2">{t('userProfile.thematicCloud.noDataDescription')}</p>
            </div>
        );
    }

    const colorClasses = { 1: 'text-sky-400', 2: 'text-teal-300', 3: 'text-gray-100', 4: 'text-purple-300', 5: 'text-amber-300 font-bold' };
    return (
        <div className="mt-6 p-4 bg-gray-800/50 rounded-lg border border-sky-500/20 animate-fadeIn">
            <h3 className="text-xl font-bold text-sky-200 font-sans mb-4">{t('userProfile.thematicCloud.title')}</h3>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-4 p-4">
                {cloud.themes.map(({ theme, weight }) => (
                    <span
                        key={theme}
                        className={`transition-all duration-300 ${colorClasses[weight]} ${onThemeClick ? 'cursor-pointer hover:opacity-100 hover:scale-110' : ''}`}
                        style={{ fontSize: `${1 + (weight / 5) * 1.25}rem`, opacity: 0.6 + (weight / 5) * 0.4 }}
                        onClick={() => onThemeClick && onThemeClick(theme)}
                    >
                        {theme}
                    </span>
                ))}
            </div>
        </div>
    );
};

// Placeholder Lottie animation data - actual JSON data needs to be imported here
const heartAnimation = null;
const laughAnimation = null;

const PollDisplay = ({ poll }) => {
    const [voted, setVoted] = useState(false);

    const handleVote = (option) => {
        if (!voted) {
            console.log("Voted for:", option);
            setVoted(true);
        }
    };

    return (
        <div className="mt-4">
            <h4 className="font-bold text-white mb-2">{poll.question}</h4>
            <div className="space-y-2">
                {poll.options.map((option, index) => (
                    <button key={index} onClick={() => handleVote(option)} disabled={voted} className="w-full text-left p-2 bg-gray-700/50 rounded-lg border border-gray-600 hover:bg-blue-500/20 disabled:opacity-70">
                        <p>{option}</p>
                    </button>
                ))}
            </div>
        </div>
    );
};



const MyWhispersTab = ({ whispers, loadingStates, currentUserProfile, onSpotlight, onAiAction, onDelete, showConfirmation, onPin, t, onRemix, onBookmark, onShowInteractions, onComment, onAiToolsClick, setIsAiMenuOpen }) => {
    const { LucideIcons } = useAppContext();
    if (whispers.length === 0) return (
        <div className="text-center text-gray-300 p-8 bg-gray-800/50 rounded-lg">
            <LucideIcons.Archive size={48} className="mx-auto mb-4 text-blue-400" />
            <h3 className="text-xl font-bold">{t('userProfile.myWhispers.emptyArchiveTitle')}</h3>
            <p className="mt-2">{t('userProfile.myWhispers.emptyArchiveDescription')}</p>
        </div>
    );
    return <div className="scroll-snap-container">{whispers.map(entry => <div key={entry.id} className="scroll-snap-align-start"><WhisperCard entry={entry} user={currentUserProfile} isSelf={true} onPin={onPin} onSpotlight={onSpotlight} onAiAction={onAiAction} onDelete={onDelete} showConfirmation={showConfirmation} t={t} onRemix={onRemix} onShowInteractions={onShowInteractions} onComment={onComment} onAiToolsClick={onAiToolsClick} setIsAiMenuOpen={setIsAiMenuOpen} /></div>)}</div>;
};

const ProfileWhispersTab = ({ whispers, t, profileUser, onRemix, onBookmark, onShowInteractions, onComment, onAiToolsClick, setIsAiMenuOpen }) => {
    const { LucideIcons } = useAppContext();
    if (whispers.length === 0) return (
        <div className="text-center text-gray-300 p-8 bg-gray-800/50 rounded-lg">
            <LucideIcons.Wind size={48} className="mx-auto mb-4 text-blue-400" />
            <h3 className="text-xl font-bold">{t('userProfile.profileWhispers.emptyTitle')}</h3>
            <p className="mt-2">{t('userProfile.profileWhispers.emptyDescription')}</p>
        </div>
    );
    return <div className="scroll-snap-container">{whispers.map(entry => <div className="scroll-snap-align-start"><WhisperCard key={entry.id} entry={entry} user={profileUser} isSelf={false} t={t} onRemix={onRemix} onShowInteractions={onShowInteractions} onComment={onComment} onAiToolsClick={onAiToolsClick} setIsAiMenuOpen={setIsAiMenuOpen} /></div>)}</div>;
};

const ProfileMediaTab = ({ whispers, t }) => {
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    // The logic to flatten media items for the gallery and lightbox
    const flattenedMediaItems = useMemo(() => {
        const allMedia = [];
        whispers.forEach(whisper => {
            if (whisper.mediaUrls && whisper.mediaUrls.length > 0) {
                whisper.mediaUrls.forEach((url, index) => {
                    // Ensure we only try to display actual image files
                    if (typeof url === 'string' && url.match(/\.(jpeg|jpg|gif|png|webp)/i)) {
                        allMedia.push({
                            ...whisper,
                            // Create a unique ID for each media item for React keys
                            uniqueId: `${whisper.id}-${index}`,
                            // Provide a direct URL for the MediaCard and Lightbox
                            displayUrl: url,
                        });
                    }
                });
            }
        });
        return allMedia;
    }, [whispers]);

    if (flattenedMediaItems.length === 0) {
        return <p className="text-center text-gray-400 italic py-8">{t('userProfile.profileMedia.noMedia')}</p>;
    }

    const slides = flattenedMediaItems.map(item => ({
        src: item.displayUrl,
        title: item.content,
    }));

    const handleCardClick = (index) => {
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    return (
        <>
            <Masonry
                breakpointCols={{ default: 4, 1100: 3, 700: 2, 500: 1 }}
                className="my-masonry-grid"
                columnClassName="my-masonry-grid_column"
            >
                {flattenedMediaItems.map((item, i) => (
                    <MediaCard
                        key={item.uniqueId}
                        whisper={item}
                        onClick={() => handleCardClick(i)}
                        t={t}
                    />
                ))}
            </Masonry>
            <Lightbox
                open={lightboxOpen}
                close={() => setLightboxOpen(false)}
                slides={slides}
                index={lightboxIndex}
            />
        </>
    );
};

const ProfileNexusesTab = ({ nexuses, t }) => {
    if (nexuses.length === 0) return <p className="text-center text-gray-400 italic py-8">{t('userProfile.profileNexuses.noNexuses')}</p>;
    return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{nexuses.map(nexus => <NexusCard key={nexus.id} nexus={nexus} />)}</div>;
};

const BookmarksTab = ({ bookmarks, t, onBookmark, setIsAiMenuOpen }) => {
    if (bookmarks.length === 0) return <p className="text-center text-gray-400 italic py-8">{t('userProfile.bookmarks.noBookmarks')}</p>;
    return <div className="space-y-4">{bookmarks.map(entry => <WhisperCard key={entry.id} entry={entry} user={entry.author} isSelf={false} t={t} onBookmark={onBookmark} setIsAiMenuOpen={setIsAiMenuOpen} />)}</div>;
};

const AdminBadgesTab = ({ allBadges, userBadges, onToggle, t }) => {
    const { LucideIcons } = useAppContext();
    return (
        <div className="p-4 bg-gray-800/50 rounded-lg">
            <h3 className="text-xl font-bold text-yellow-300 mb-4">{t('userProfile.adminBadges.title')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(allBadges).map(([badgeId, badgeData]) => {
                    const hasBadge = userBadges.includes(badgeId);
                    const Icon = badgeData.icon;
                    return (
                        <div key={badgeId} className={`p-3 rounded-lg flex items-center gap-3 transition-all ${hasBadge ? 'bg-green-500/20 border-green-500' : 'bg-gray-700/50 border-gray-600'} border`}>
                            <Icon size={24} className={badgeData.color} />
                            <span className="flex-grow text-sm">{badgeData.name}</span>
                            <button onClick={() => onToggle(badgeId, !hasBadge)} className={`px-3 py-1 rounded-full text-xs font-bold ${hasBadge ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}>
                                {hasBadge ? t('userProfile.adminBadges.remove') : t('userProfile.adminBadges.grant')}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const AdminThemesTab = ({ allThemes, userThemes, onToggle, t }) => {
    return (
        <div className="p-4 bg-gray-800/50 rounded-lg mt-4">
            <h3 className="text-xl font-bold text-purple-300 mb-4">{t('userProfile.adminThemes.title') || "Manage Themes"}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(allThemes).map(([themeId, themeData]) => {
                    const hasTheme = userThemes.includes(themeId);
                    return (
                        <div key={themeId} className={`p-3 rounded-lg flex items-center gap-3 transition-all ${hasTheme ? 'bg-green-500/20 border-green-500' : 'bg-gray-700/50 border-gray-600'} border`}>
                            <div className={`w-8 h-8 rounded-full ${themeData.previewClass} border border-gray-400`}></div>
                            <span className="flex-grow text-sm">{themeData.name}</span>
                            <button onClick={() => onToggle(themeId, !hasTheme)} className={`px-3 py-1 rounded-full text-xs font-bold ${hasTheme ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}>
                                {hasTheme ? (t('userProfile.adminBadges.remove') || "Revoke") : (t('userProfile.adminBadges.grant') || "Grant")}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const ProfileBadgesTab = ({ badgeIds, isSelf, pinnedBadgeIds, onPinBadge, t, badgeConfig }) => {
    const { LucideIcons } = useAppContext();
    const [flippedBadge, setFlippedBadge] = useState(null);

    if (!badgeIds || badgeIds.length === 0) {
        return <p className="text-center text-gray-400 italic py-8">{t('userProfile.profileBadges.noBadges')}</p>;
    }

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {badgeIds.map(badgeId => {
                const badge = badgeConfig[badgeId];
                if (!badge) return null;

                const Icon = badge.icon;
                const isFlipped = flippedBadge === badgeId;
                const isPinned = pinnedBadgeIds.includes(badgeId);

                const handleCardClick = (e) => {
                    // Prevent flipping when clicking the pin button
                    if (e.target.closest('.pin-button')) return;
                    setFlippedBadge(isFlipped ? null : badgeId);
                };

                return (
                    <div key={badgeId} className={`badge-card ${isFlipped ? 'flipped' : ''}`} onClick={handleCardClick}>
                        <div className="badge-card-inner">
                            <div className="badge-card-front relative p-4 bg-gray-800/50 rounded-lg flex items-center gap-4 border border-gray-700">
                                <Icon size={32} className={badge.color} />
                                <div>
                                    <p className={`font-bold ${badge.color}`}>{badge.name}</p>
                                </div>
                                {isSelf && (
                                    <button
                                        onClick={() => onPinBadge(badgeId)}
                                        className={`pin-button absolute top-2 right-2 p-1.5 rounded-full transition-colors duration-300 ${isPinned ? 'bg-yellow-500/80 text-white' : 'bg-gray-600/50 text-gray-300 hover:bg-yellow-500/50'}`}
                                        aria-label={isPinned ? 'Unpin Badge' : 'Pin Badge'}
                                    >
                                        <LucideIcons.Pin size={14} className={`transition-transform duration-300 ${isPinned ? 'rotate-45' : ''}`} />
                                    </button>
                                )}
                            </div>
                            <div className="badge-card-back p-4 bg-gray-700/50 rounded-lg flex flex-col items-center justify-center text-center gap-2 border border-gray-600">
                                <h4 className="font-bold text-white">{badge.name}</h4>
                                <p className="text-xs text-gray-300">{badge.description}</p>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const ThemesTab = ({ profileUser, isSelf, t }) => {
    const { LucideIcons, updateUserProfile, updateUserProfileInState, setMessage, appFunctions } = useAppContext();
    const [selectedTheme, setSelectedTheme] = useState(profileUser?.profileTheme || 'default');
    const [showThemePurchaseModal, setShowThemePurchaseModal] = useState(false);
    const [themeToBuy, setThemeToBuy] = useState(null);

    const profileThemes = getProfileThemes(t);
    const userBadges = new Set(profileUser.badges || []);
    const ownedThemes = new Set(profileUser.ownedThemes || []);

    const handleThemeSelect = (themeId, theme) => {
        if (!isSelf) return;

        if (theme.isPaid && !ownedThemes.has(themeId)) {
            setThemeToBuy({ id: themeId, ...theme });
            setShowThemePurchaseModal(true);
            return;
        }

        setSelectedTheme(themeId);
        updateUserProfile(profileUser.id, { profileTheme: themeId });
        updateUserProfileInState(profileUser.id, { profileTheme: themeId });
        setMessage(t('profileCustomization.themeUpdateSuccess'));
    };

    const handleBuyTheme = async (themeId, price) => {
        const createPayPalOrder = httpsCallable(appFunctions, 'createPayPalOrder');
        try {
            const result = await createPayPalOrder({ itemId: themeId, type: 'theme' });
            return result.data.orderID;
        } catch (error) {
            setMessage(t('walletHub.purchaseError', { error: error.message }));
            throw error;
        }
    };

    const onThemeApproved = async (data) => {
        const capturePayPalOrder = httpsCallable(appFunctions, 'capturePayPalOrder');
        try {
            const result = await capturePayPalOrder({ orderID: data.orderID });
            if (result.data.success) {
                setMessage(t('themePurchase.success') || "Theme purchased successfully!");
                setShowThemePurchaseModal(false);
                // Optimistically update owned themes if possible, or wait for snapshot
            }
        } catch (error) {
            console.error("Error capturing PayPal order:", error);
            setMessage(t('walletHub.purchaseFailed', { message: error.message }));
        }
    };

    return (
        <div className="p-4">
            {showThemePurchaseModal && themeToBuy && (
                <ThemePurchaseModal 
                    onClose={() => setShowThemePurchaseModal(false)}
                    handleBuyTheme={handleBuyTheme}
                    onApprove={onThemeApproved}
                    themeId={themeToBuy.itemId}
                    themeName={themeToBuy.name}
                    price={themeToBuy.price}
                    previewClass={themeToBuy.previewClass}
                />
            )}
            <h3 className="text-xl font-bold text-white mb-4">{t('userProfile.tabs.themes')}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {Object.entries(profileThemes).map(([id, theme]) => {
                    let isUnlocked = true;
                    let lockReason = "";

                    if (theme.requiredBadge && !userBadges.has(theme.requiredBadge)) {
                        isUnlocked = false;
                        lockReason = t('profileCustomization.requiresBadge', { badgeName: theme.requiredBadge.replace(/_/g, ' ') });
                    } else if (theme.requiresFounder && !profileUser.isFounder) {
                        isUnlocked = false;
                        lockReason = t('profileCustomization.requiresFounder');
                    } else if (theme.requiredWhispers && (profileUser.stats?.whispers || 0) < theme.requiredWhispers) {
                        isUnlocked = false;
                        lockReason = theme.unlockHint;
                    } else if (theme.requiredDays) {
                        const daysMember = profileUser.joinedAt && profileUser.joinedAt.toDate ? (Date.now() - profileUser.joinedAt.toDate().getTime()) / (1000 * 60 * 60 * 24) : 0;
                        if (daysMember < theme.requiredDays) {
                            isUnlocked = false;
                            lockReason = theme.unlockHint;
                        }
                    } else if (theme.requiredReputation && (profileUser.reputationScore || 0) < theme.requiredReputation) {
                         isUnlocked = false;
                         lockReason = theme.unlockHint;
                    } else if (theme.requiredConnections && (profileUser.stats?.connections || 0) < theme.requiredConnections) {
                         isUnlocked = false;
                         lockReason = theme.unlockHint;
                    }

                    const isOwned = ownedThemes.has(id);
                    const canBuy = theme.isPaid && !isOwned;

                    // Allow clicking if unlocked OR if it's a paid theme that can be bought
                    const isClickable = (isUnlocked || canBuy) && isSelf;
                    // It is "locked" visually if requirements not met AND not buyable (or buyable but requirements not met?)
                    // Assuming paid themes don't have other requirements (or if they do, they must be met first)
                    // Logic: If requirements NOT met, it is locked regardless of payment.
                    // If requirements met, but paid and not owned, it shows price tag.

                    return (
                        <div 
                            key={id} 
                            onClick={() => isClickable && isUnlocked && handleThemeSelect(id, theme)} 
                            className={`theme-option relative ${(!isUnlocked) ? 'locked' : ''} ${selectedTheme === id ? 'selected' : ''} ${isClickable && isUnlocked ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                        >
                            <div className={`theme-preview ${theme.previewClass}`}></div>
                            <span className="theme-name">{theme.name}</span>
                            {!isUnlocked && (
                                <div className="lock-icon">
                                    <LucideIcons.Lock size={16} />
                                    <span className="tooltip-text">{lockReason}</span>
                                </div>
                            )}
                            {isUnlocked && canBuy && (
                                <div className="absolute top-2 right-2 bg-green-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg z-10">
                                    ${theme.price}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const ProfileConstellationTab = ({ targetUserId, db, appId, userProfiles }) => {
    const [connections, setConnections] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchConnections = async () => {
            try {
                const q = query(collection(db, `artifacts/${appId}/users/${targetUserId}/connections`));
                const snapshot = await getDocs(q);
                const connectionData = snapshot.docs.map(doc => doc.data());

                const centerProfile = userProfiles.find(p => p.id === targetUserId);
                const mappedProfiles = [];
                
                if (centerProfile) {
                    mappedProfiles.push({ ...centerProfile, connection: null });
                }

                // Create a Set for faster lookup and to avoid duplicates if any
                const addedIds = new Set([targetUserId]);

                connectionData.forEach(conn => {
                    const profile = userProfiles.find(p => p.id === conn.followingId);
                    if (profile && !addedIds.has(profile.id)) {
                        mappedProfiles.push({ ...profile, connection: conn });
                        addedIds.add(profile.id);
                    }
                });

                setConnections(mappedProfiles);
            } catch (error) {
                console.error("Error fetching constellation:", error);
            } finally {
                setIsLoading(false);
            }
        };

        if (targetUserId && userProfiles.length > 0) {
            fetchConnections();
        }
    }, [targetUserId, db, appId, userProfiles]);

    if (isLoading) return <LoadingSpinner />;
    
    if (connections.length <= 1) {
        return (
            <div className="text-center text-gray-400 italic py-16 bg-gray-800/30 rounded-lg">
                <p>No constellation data visible.</p>
            </div>
        );
    }

    return (
        <div className="h-[60vh] w-full bg-black/40 rounded-xl overflow-hidden border border-gray-800">
             <InteractiveConstellation 
                connections={connections} 
                interConnections={[]} 
                onSelectStar={() => {}}
                centerUserId={targetUserId}
            />
        </div>
    );
};

function UserProfile({ profileUserId, onMessageUser }) {
    const { t, i18n } = useTranslation();
    const { userId, userProfiles, db, appFunctions, setMessage, uploadFile, showConfirmation, TOKEN_COSTS, currentUserProfile, updateUserProfileInState, setShowAuraChamber, setShowProModal, appId, updateUserProfile, handleUserSelect, badgeConfig, userConnections, setUserConnections, setMediaToView } = useAppContext();

    const onToggleConnection = async (targetUserId, isConnected) => {
        const action = isConnected ? 'unfollowUser' : 'followUser';
        const actionFunc = httpsCallable(appFunctions, action);

        try {
            await actionFunc({ userIdToFollow: targetUserId, userIdToUnfollow: targetUserId });
            if (isConnected) {
                setUserConnections(prev => prev.filter(c => c.followingId !== targetUserId));
            } else {
                setUserConnections(prev => [...prev, { followingId: targetUserId }]);
            }
        } catch (error) {
            setMessage(error.message);
        }
    };

    const profileUser = userProfiles.find(p => p.id === profileUserId);
    const isSelf = userId === profileUserId;
    const isConnected = userConnections.some(c => c.followingId === profileUserId);
    const canModerate = currentUserProfile && ['moderator', 'admin', 'owner'].includes(currentUserProfile.role);

    const isProfilePrivate = profileUser?.privacySettings?.profileVisibility === 'private';
    const canViewProfile = !isProfilePrivate || isConnected || isSelf || canModerate;

    const [showEditProfileModal, setShowEditProfileModal] = useState(false);
    const [showActionsDropdown, setShowActionsDropdown] = useState(false);

    useEffect(() => {
        if (!showActionsDropdown) return;
        const close = () => setShowActionsDropdown(false);
        window.addEventListener('click', close);
        return () => window.removeEventListener('click', close);
    }, [showActionsDropdown]);

    const [userPublicWhispers, setUserPublicWhispers] = useState([]);
    const [lastVisibleWhisper, setLastVisibleWhisper] = useState(null);
    const [hasMoreWhispers, setHasMoreWhispers] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [userMediaItems, setUserMediaItems] = useState([]);
    const [isLoadingMedia, setIsLoadingMedia] = useState(false);
    const [userHighlights, setUserHighlights] = useState([]);
    const [isLoadingHighlights, setIsLoadingHighlights] = useState(false);
    const [activeTab, setActiveTab] = useState('content');
    const [activeSubTab, setActiveSubTab] = useState('published');
    const [searchTerm, setSearchTerm] = useState('');
    const [loadingStates, setLoadingStates] = useState({});
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const [aiModalContent, setAiModalContent] = useState({ title: '', content: '' });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [personalitySnapshot, setPersonalitySnapshot] = useState(null);
    const [isLoadingSnapshot, setIsLoadingSnapshot] = useState(false);
    const [bioSuggestions, setBioSuggestions] = useState([]);
    const [showBioModal, setShowBioModal] = useState(false);
    const [isEnhancingBio, setIsEnhancingBio] = useState(false);
    const [profileInsight, setProfileInsight] = useState(null);
    const [soulBlueprint, setSoulBlueprint] = useState(null);
    const [isLoadingInsight, setIsLoadingInsight] = useState(false);
    const [isLoadingBlueprint, setIsLoadingBlueprint] = useState(false);
    const [userNexuses, setUserNexuses] = useState([]);
    const [statModal, setStatModal] = useState({ isOpen: false, title: '', value: 0, history: [] });

    useEffect(() => {
        if (!profileUserId) return;

        const fetchUserNexuses = async () => {
            try {
                const nexusesRef = collection(db, `artifacts/${appId}/public/data/nexuses`);
                const q = query(nexusesRef, where("memberIds", "array-contains", profileUserId));
                const querySnapshot = await getDocs(q);
                const fetchedNexuses = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setUserNexuses(fetchedNexuses);
            } catch (error) {
                console.error("Error fetching user nexuses:", error);
                setMessage("Could not load user nexuses.");
            }
        };

        fetchUserNexuses();
    }, [profileUserId, db, appId, setMessage]);

    const [statHistory, setStatHistory] = useState([]);

    const openStatModal = async (title, value, statName) => {
        setStatModal({ isOpen: true, title, value, history: [] });
        const historyRef = collection(db, `artifacts/${appId}/user_stats_history/${profileUserId}/daily`);
        const q = query(historyRef, orderBy("timestamp", "desc"), limit(30));
        const snapshot = await getDocs(q);
        const historyData = snapshot.docs.map(doc => ({
            date: doc.id,
            value: doc.data()[statName]
        }));
        setStatHistory(historyData.reverse());
    };

    const [humanReadableLocation, setHumanReadableLocation] = useState(null);
    const [pinnedWhisper, setPinnedWhisper] = useState(null);
    const [thematicCloud, setThematicCloud] = useState(null);
    const [isLoadingThematicCloud, setIsLoadingThematicCloud] = useState(false);
    const [themeFilter, setThemeFilter] = useState(null);
    const [conversationStarters, setConversationStarters] = useState([]);
    const [isLoadingStarters, setIsLoadingStarters] = useState(false);
    const [auraComparisonData, setAuraComparisonData] = useState(null);
    const [showAuraComparison, setShowAuraComparison] = useState(false);
    const [pinnedBadgeIds, setPinnedBadgeIds] = useState([]);
    const [showCreateContentModal, setShowCreateContentModal] = useState(false);
    const [remixingWhisper, setRemixingWhisper] = useState(null);
    const [initialMediaForWhisper, setInitialMediaForWhisper] = useState(null);
    const [showBadgeInfoModal, setShowBadgeInfoModal] = useState(false);
    const [interactionModal, setInteractionModal] = useState({ isOpen: false, users: [], title: '' });
    const [commentingWhisper, setCommentingWhisper] = useState(null);
    const [aiToolsWhisper, setAiToolsWhisper] = useState(null);
    const [aiToolsPosition, setAiToolsPosition] = useState({ top: 0, left: 0 });
    const [isAiMenuOpen, setIsAiMenuOpen] = useState(false);
    const [showShowdownModal, setShowShowdownModal] = useState(false);

    const handleAiToolsClick = (whisper, buttonRef) => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setAiToolsPosition({ top: rect.top, left: rect.left });
        }
        setAiToolsWhisper(whisper);
    };

    const handleShowInteractions = async (whisperId, interactionType) => {
        try {
            const getInteractionUsers = httpsCallable(appFunctions, 'getInteractionUsers');
            const result = await getInteractionUsers({ whisperId, interactionType });
            const users = result.data;
            const title = interactionType === 'echoes' ? t('userProfile.interactionModal.echoedBy') : t('userProfile.interactionModal.reWhisperedBy');
            setInteractionModal({ isOpen: true, users, title });
        } catch (error) {
            setMessage(`Error fetching users: ${error.message}`);
        }
    };

    const fetchWhispers = useCallback(async (loadMore = false, cursor = null) => {
        if (!profileUserId) return;
        if (loadMore) setIsLoadingMore(true); else setIsLoading(true);
        let q = query(collection(db, `artifacts/${appId}/public/data/whispers`), where("authorId", "==", profileUserId), orderBy("timestamp", "desc"), limit(9));
        if (loadMore && cursor) q = query(q, startAfter(cursor));
        try {
            const whispersSnapshot = await getDocs(q);
            const newWhispers = whispersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setLastVisibleWhisper(whispersSnapshot.docs[whispersSnapshot.docs.length - 1] || null);
            setHasMoreWhispers(newWhispers.length === 9);
            setUserPublicWhispers(prev => loadMore ? [...prev, ...newWhispers] : newWhispers);
        } catch (error) {
            console.error("Error fetching whispers:", error);
            setMessage("Could not load whispers.");
        } finally {
            if (!loadMore) setIsLoading(false);
            setIsLoadingMore(false);
        }
    }, [profileUserId, db, appId, setMessage]);

    const fetchAllMediaWhispers = useCallback(async () => {
        if (!profileUserId) return;
        setIsLoadingMedia(true);
        try {
            // First, try the efficient query for new posts
            let mediaItems = [];
            const qNew = query(
                collection(db, `artifacts/${appId}/public/data/whispers`),
                where("authorId", "==", profileUserId),
                where("hasMedia", "==", true),
                orderBy("timestamp", "desc")
            );
            const querySnapshotNew = await getDocs(qNew);
            mediaItems = querySnapshotNew.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // As a fallback for older data, fetch all and filter client-side.
            // This is less efficient but ensures backward compatibility.
            const qAll = query(
                collection(db, `artifacts/${appId}/public/data/whispers`),
                where("authorId", "==", profileUserId),
                orderBy("timestamp", "desc")
            );
            const querySnapshotAll = await getDocs(qAll);
            const allUserWhispers = querySnapshotAll.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            const oldMediaItems = allUserWhispers.filter(whisper => 
                !whisper.hasMedia && // Ensure we don't duplicate items from the new query
                whisper.mediaUrls && 
                whisper.mediaUrls.length > 0
            );

            // Combine and remove duplicates, just in case
            const combined = [...mediaItems, ...oldMediaItems];
            const uniqueMediaItems = Array.from(new Map(combined.map(item => [item.id, item])).values());
            
            setUserMediaItems(uniqueMediaItems);

        } catch (error) {
            console.error("Error fetching media whispers:", error);
            setMessage("Could not load media gallery.");
        } finally {
            setIsLoadingMedia(false);
        }
    }, [profileUserId, db, appId, setMessage]);

    const fetchUserHighlights = useCallback(async () => {
        if (!profileUserId) return;
        setIsLoadingHighlights(true);
        try {
            const highlightsRef = collection(db, `artifacts/${appId}/users/${profileUserId}/highlights`);
            const q = query(highlightsRef, orderBy("archivedAt", "desc"));
            const querySnapshot = await getDocs(q);
            const fetchedHighlights = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setUserHighlights(fetchedHighlights);
        } catch (error) {
            console.error("Error fetching user highlights:", error);
            setMessage("Could not load highlights.");
        } finally {
            setIsLoadingHighlights(false);
        }
    }, [profileUserId, db, appId, setMessage]);

    const { ref, inView } = useInView();

    const handleGetThematicCloud = useCallback(async () => {
        setIsLoadingThematicCloud(true);
        try {
            const result = await httpsCallable(appFunctions, 'getThematicCloud')({ targetUserId: profileUserId });
            setThematicCloud(result.data);
        } catch (error) {
            setMessage(`Thematic Cloud analysis failed: ${error.message}`);
        } finally {
            setIsLoadingThematicCloud(false);
        }
    }, [appFunctions, profileUserId, setMessage]);

    useEffect(() => {
        if (inView && hasMoreWhispers && !isLoadingMore) {
            fetchWhispers(true, lastVisibleWhisper);
        }
    }, [inView, hasMoreWhispers, isLoadingMore, fetchWhispers, lastVisibleWhisper]);

    useEffect(() => {
        if (activeTab === 'content') {
            if (activeSubTab === 'published') {
                fetchWhispers(false, null);
            } else if (activeSubTab === 'media') {
                fetchAllMediaWhispers();
            } else if (activeSubTab === 'highlights') {
                fetchUserHighlights();
            }
        }
        if (activeTab === 'insights') {
            if (activeSubTab === 'journey') {
                // Journey tab logic if needed
            } else if (activeSubTab === 'thematicCloud') {
                handleGetThematicCloud();
            }
        }
    }, [activeTab, activeSubTab, isSelf, handleGetThematicCloud, fetchWhispers, fetchAllMediaWhispers, fetchUserHighlights]);

    const onRemix = (whisper) => {
        setRemixingWhisper(whisper);
        setShowCreateContentModal(true);
    };

    const onWhisperFromMedia = (mediaItem) => {
        setInitialMediaForWhisper(mediaItem);
        setShowCreateContentModal(true);
    };

    const handleEditDraft = (draft) => {
        // Logic to open the whisper creation modal with the draft content
        console.log("Editing draft:", draft);
    };

    const handlePublishDraft = async (draft) => {
        // Logic to publish the draft
        console.log("Publishing draft:", draft);
    };

    const handleDeleteDraft = async (draftId) => {
        // Logic to delete the draft
        console.log("Deleting draft:", draftId);
    };

    const handleBookmark = async (whisperId, remove = false) => {
        const toggleBookmark = httpsCallable(appFunctions, 'toggleBookmark');
        try {
            await toggleBookmark({ whisperId });
            if (remove) {
                // setUserBookmarks(prev => prev.filter(b => b.id !== whisperId)); // Removed as per user request
                setMessage(t('whisperCard.bookmarkRemoved'));
            } else {
                setMessage(t('whisperCard.bookmarkAdded'));
            }
        } catch (error) {
            setMessage(t('whisperCard.bookmarkError', { message: error.message }));
        }
    };


    const reputationLevels = getReputationLevels(t);
    const influenceLevels = getInfluenceLevels(t);

    const { nextLevel: nextRepLevel } = getNextLevel(profileUser?.reputationScore || 0, reputationLevels);
    const { nextLevel: nextInfLevel } = getNextLevel(profileUser?.influenceScore || 0, influenceLevels);

    const mutualConnections = useMemo(() => {
        if (!currentUserProfile || !profileUser || isSelf) return [];
        const myConnections = currentUserProfile.connections?.map(c => c.followingId) || [];
        const theirConnections = profileUser.connections?.map(c => c.followingId) || [];
        const mutualIds = myConnections.filter(id => theirConnections.includes(id));
        return userProfiles.filter(p => mutualIds.includes(p.id));
    }, [currentUserProfile, profileUser, userProfiles, isSelf]);

    const handlePinBadge = (badgeId) => {
        setPinnedBadgeIds(prev => {
            const newPinned = prev.includes(badgeId)
                ? prev.filter(id => id !== badgeId)
                : [...prev, badgeId];

            if (newPinned.length > 5) {
                setMessage(t('userProfile.strings.maxPinnedBadges'));
                return prev;
            }
            
            updateUserProfile(userId, { pinnedBadges: newPinned });
            return newPinned;
        });
    };

    const handleToggleBadge = async (badgeId, grant) => {
        try {
            const toggleBadge = httpsCallable(appFunctions, 'adminToggleBadge');
            await toggleBadge({ targetUserId: profileUserId, badgeId, grant });
            
            // Manually update the local state to reflect the change immediately
            const updatedBadges = grant
                ? [...(profileUser.badges || []), badgeId]
                : (profileUser.badges || []).filter(b => b !== badgeId);
            
            updateUserProfileInState(profileUserId, { badges: updatedBadges });

            setMessage(grant ? t('userProfile.success.badgeToggled_granted') : t('userProfile.success.badgeToggled_revoked'));
        } catch (error) {
            setMessage(t('userProfile.errors.toggleBadge', { message: error.message }));
        }
    };

    const handleToggleTheme = async (themeId, grant) => {
        try {
            const updateData = {
                ownedThemes: grant ? arrayUnion(themeId) : arrayRemove(themeId)
            };
            await updateUserProfile(profileUserId, updateData);
            
            const updatedThemes = grant 
                ? [...(profileUser.ownedThemes || []), themeId]
                : (profileUser.ownedThemes || []).filter(t => t !== themeId);
            
            updateUserProfileInState(profileUserId, { ownedThemes: updatedThemes });
            setMessage(grant ? (t('userProfile.success.themeGranted') || "Theme granted") : (t('userProfile.success.themeRevoked') || "Theme revoked"));
        } catch (error) {
            setMessage(t('userProfile.errors.toggleTheme', { message: error.message }) || `Error: ${error.message}`);
        }
    };

    const handlePinWhisper = (whisper) => {
        setPinnedWhisper(whisper);
        setMessage(t('userProfile.success.whisperPinned'));
    };

    const handleGetConversationStarters = useCallback(async () => {
        setIsLoadingStarters(true);
        try {
            const result = await httpsCallable(appFunctions, 'getConversationStarters')({ targetUserId: profileUserId });
            setConversationStarters(result.data.starters);
            setAiModalContent({ title: t('userProfile.strings.aiConversationStarters'), content: result.data.starters.join('\n\n') });
            setIsModalOpen(true);
        } catch (error) {
            setMessage(t('userProfile.errors.getConversationStarters', { message: error.message }));
        } finally {
            setIsLoadingStarters(false);
        }
    }, [appFunctions, profileUserId, setMessage, t]);



    useEffect(() => {
        if (profileUser) {
            setPinnedBadgeIds(profileUser.pinnedBadges || []);
            if (profileUser.pinnedWhisperId) {
                const fetchPinned = async () => {
                    const whisperRef = doc(db, `artifacts/${appId}/public/data/whispers`, profileUser.pinnedWhisperId);
                    const whisperSnap = await getDocs(whisperRef);
                    if (whisperSnap.exists()) {
                        setPinnedWhisper({ id: whisperSnap.id, ...whisperSnap.data() });
                    }
                };
                fetchPinned();
            }
        }
    }, [profileUser, db, appId]);



    useEffect(() => {
        if (profileUser?.location?.latitude && profileUser?.location?.longitude) {
            const fetchLocation = async () => {
                try {
                    const reverseGeocode = httpsCallable(appFunctions, 'reverseGeocodeLocation');
                    const result = await reverseGeocode({
                        latitude: profileUser.location.latitude,
                        longitude: profileUser.location.longitude,
                    });
                    setHumanReadableLocation(result.data.formattedAddress);
                } catch (error) {
                    console.error("Error reverse geocoding location:", error);
                    setMessage(t('userProfile.errors.reverseGeocode'));
                }
            };
            fetchLocation();
        } else {
            setHumanReadableLocation(null);
        }
    }, [profileUser?.location?.latitude, profileUser?.location?.longitude, appFunctions, setMessage, t]);

    const handleDeleteWhisper = useCallback(async (entryId) => {
        const deleteContent = httpsCallable(appFunctions, 'deleteContent');
        try {
            await deleteContent({ contentId: entryId, contentType: 'whisper' });
            setMessage(t('userProfile.success.whisperDeleted'));
            setUserPublicWhispers(prev => prev.filter(w => w.id !== entryId));
        } catch (e) { 
            setMessage(t('userProfile.errors.deleteWhisper', { message: e.message })); 
        }
    }, [appFunctions, setMessage, t]);

    const handleWhisperAiAction = useCallback(async (actionType, entry) => {
        setLoadingStates(prev => ({ ...prev, [entry.id]: true }));
        const analysisMap = {
            'PUBLIC_SUMMARY': t('aiTools.summarize'),
            'TONE_ANALYSIS': t('aiTools.analyzeTone'),
            'GET_VIBE': t('aiTools.getVibe'),
            'AI_EXPLAIN': t('aiTools.explain'),
            'SUGGEST_COMMENT_REPLY': t('aiTools.suggestReply'),
            'KEY_POINTS': t('aiTools.keyPoints'),
            'PERSPECTIVE_SHIFT': t('aiTools.shiftPerspective'),
            'SUGGEST_QUESTION': t('aiTools.suggestQuestion'),
            'GET_AURA_INSIGHT': t('aiTools.getAuraInsight'),
        };
        try {
            const getAnalysis = httpsCallable(appFunctions, 'getAiAnalysis');
            const result = await getAnalysis({ entryId: entry.id, analysisType: actionType, content: entry.content, authorId: entry.authorId });
            setAiModalContent({ title: analysisMap[actionType] || actionType, content: result.data.text });
            setIsModalOpen(true);
        } catch (e) { 
            setMessage(t('userProfile.errors.aiAction', { actionType }));
        } 
        finally { setLoadingStates(prev => ({ ...prev, [entry.id]: false })); } 
    }, [appFunctions, setMessage, t]);

    const handleSetSpotlight = useCallback(async (entryId) => {
        setLoadingStates(prev => ({ ...prev, [entryId]: true }));
        const setSpotlightFn = httpsCallable(appFunctions, 'setSpotlight');
        try { 
            await setSpotlightFn({ whisperId: entryId }); 
            setMessage(t('userProfile.success.spotlightSet')); 
        } 
        catch (error) { 
            setMessage(t('userProfile.errors.setSpotlight', { message: error.message })); 
        } 
        finally { setLoadingStates(prev => ({ ...prev, [entryId]: false })); }
    }, [appFunctions, setMessage, t]);

    const handleGetSnapshot = useCallback(async () => {
        setIsLoadingSnapshot(true);
        try {
            const getSnapshot = httpsCallable(appFunctions, 'getPersonalitySnapshot');
            const [currentUserSnapshot, targetUserSnapshot] = await Promise.all([
                getSnapshot({ targetUserId: userId }),
                getSnapshot({ targetUserId: profileUserId })
            ]);
            setAuraComparisonData({
                currentUserData: { ...currentUserSnapshot.data, displayName: currentUserProfile.displayName },
                targetUserData: { ...targetUserSnapshot.data, displayName: profileUser.displayName }
            });
            setShowAuraComparison(true);
        } catch (error) {
            setMessage(t('userProfile.errors.getSnapshot', { message: error.message }));
        } finally {
            setIsLoadingSnapshot(false);
        }
    }, [appFunctions, profileUserId, userId, currentUserProfile, profileUser, setMessage, t]);

    const handleEnhanceBio = useCallback(async (currentBio) => {
        setIsEnhancingBio(true);
        try {
            const result = await httpsCallable(appFunctions, 'enhanceBio')({ currentBio });
            if (result.data.suggestions?.length > 0) { 
                setBioSuggestions(result.data.suggestions); 
                setShowBioModal(true); 
            }
            else { 
                setMessage(t('userProfile.strings.noSuggestions')); 
            }
        } catch (error) { 
            setMessage(t('userProfile.errors.enhanceBio', { message: error.message })); 
        } 
        finally { setIsEnhancingBio(false); }
    }, [appFunctions, setMessage, t]);

    const handleGetProfileInsight = useCallback(async () => {
        setIsLoadingInsight(true);
        try { 
            const result = await httpsCallable(appFunctions, 'getProfileInsight')(); 
            setProfileInsight(result.data.analysis); 
        }
        catch (error) { 
            setMessage(t('userProfile.errors.getEchoesOfTomorrow', { message: error.message })); 
        } 
        finally { setIsLoadingInsight(false); }
    }, [appFunctions, setMessage, t]);

    const handleGetSoulBlueprint = useCallback(async () => {
        setIsLoadingBlueprint(true);
        try {
            const result = await httpsCallable(appFunctions, 'getSoulBlueprint')();
            setSoulBlueprint(result.data.analysis);
        } catch (error) {
            setMessage(t('userProfile.errors.getSnapshot', { message: error.message }));
        } finally {
            setIsLoadingBlueprint(false);
        }
    }, [appFunctions, setMessage, t]);

    const handleSaveProfile = async (formData) => {
        const { displayName, bio, interests, accentColor, headerImageFile, avatarFile } = formData;
        
        const newProfileData = { 
            displayName, 
            bio, 
            interests, 
            accentColor 
        };

        try {
            // 1. Upload Header Image if present
            if (headerImageFile) {
                const headerPath = `headers/${userId}/${headerImageFile.name}`;
                const headerUrl = await uploadFile(headerImageFile, headerPath, () => {});
                newProfileData.headerImageURL = headerUrl;
            }

            // 2. Upload Avatar if present
            if (avatarFile) {
                 const avatarPath = `avatars/${userId}/${avatarFile.name}`;
                 await uploadFile(avatarFile, avatarPath, () => {});
                 
                 // Call the function to update photoURL in auth and profile (if not redundant with updateUserProfile below)
                 // Note: updateUserProfile updates the profile doc. `updateProfilePicture` cloud function handles Auth + Doc.
                 // To correspond with previous logic which used `updateProfilePicture`:
                 const updateAvatarFn = httpsCallable(appFunctions, 'updateProfilePicture');
                 const result = await updateAvatarFn({ filePath: avatarPath, userId });
                 if (result.data.newUrl) {
                     newProfileData.photoURL = result.data.newUrl;
                 }
            }

            // 3. Update Profile Document
            await updateUserProfile(userId, newProfileData);
            updateUserProfileInState(userId, newProfileData);
            
            setMessage(t('userProfile.success.profileUpdated'));
        }
        catch (error) { 
            console.error("Error updating profile:", error);
            throw new Error(error.message); // Re-throw for modal to handle
        } 
    };

    const handleBanUser = () => {
        showConfirmation({
            message: t('userProfile.actions.banUserConfirmation', { displayName: profileUser.displayName }),
            onConfirm: async () => {
                try {
                    const reason = t('userProfile.strings.bannedByModerator');
                    await httpsCallable(appFunctions, 'banUser')({ targetUserId: profileUserId, reason: reason });
                    setMessage(t('userProfile.success.userBanned'));
                } catch (error) {
                    setMessage(t('userProfile.errors.banUser', { message: error.message }));
                }
            }
        });
    };

    const handleBlockUser = () => {
        showConfirmation({
            message: t('userProfile.actions.blockUserConfirmation', { displayName: profileUser.displayName }) || `Are you sure you want to block ${profileUser.displayName}? They will no longer be able to interact with you.`,
            onConfirm: async () => {
                try {
                    await httpsCallable(appFunctions, 'blockUser')({ targetUserId: profileUserId });
                    setMessage(t('userProfile.success.userBlocked') || 'User has been blocked successfully.');
                } catch (error) {
                    setMessage(t('userProfile.errors.blockUser', { message: error.message }) || `Failed to block user: ${error.message}`);
                }
            }
        });
    };

    const isInnerCircle = userConnections.some(c => c.followingId === profileUserId && c.tier === 'Inner Circle');

    const handleToggleInnerCircle = async () => {
        const action = isInnerCircle ? 'remove' : 'add';
        const funcName = isInnerCircle ? 'removeInnerCircleMember' : 'addInnerCircleMember';
        
        showConfirmation({
            message: isInnerCircle 
                ? (t('userProfile.actions.removeFromInnerCircleConfirmation') || "Remove from Inner Circle?")
                : (t('userProfile.actions.addToInnerCircleConfirmation') || "Add to Inner Circle?"),
            onConfirm: async () => {
                try {
                    const func = httpsCallable(appFunctions, funcName);
                    await func({ targetUserId: profileUserId });
                    
                    // Optimistic update
                    if (typeof setUserConnections === 'function') {
                        setUserConnections(prev => {
                            const exists = prev.some(c => c.followingId === profileUserId);
                            if (exists) {
                                return prev.map(c => {
                                    if (c.followingId === profileUserId) {
                                        return { ...c, tier: isInnerCircle ? 'Following' : 'Inner Circle' };
                                    }
                                    return c;
                                });
                            } else if (!isInnerCircle) {
                                 // If adding and connection didn't exist (edge case), add it
                                 return [...prev, { followingId: profileUserId, tier: 'Inner Circle' }];
                            }
                            return prev;
                        });
                    }

                    setMessage(isInnerCircle 
                        ? (t('userProfile.success.removedFromInnerCircle') || "Removed from Inner Circle")
                        : (t('userProfile.success.addedToInnerCircle') || "Added to Inner Circle")
                    );
                } catch (error) {
                    setMessage(t('userProfile.errors.toggleInnerCircle', { message: error.message }) || `Error: ${error.message}`);
                }
            }
        });
    };

    const isPro = currentUserProfile?.proStatus === 'active';
    const freeScansUsed = currentUserProfile?.freeAuraScansUsed || 0;

    const handleAuraClick = () => {
        if (!isPro && freeScansUsed >= 1) {
            showConfirmation({
                message: t('userProfile.strings.proPrompt'),
                onConfirm: () => setShowProModal(true)
            });
        } else {
            setShowAuraChamber(true);
        }
    };

    const filteredWhispers = useMemo(() => userPublicWhispers.filter(e => !e.isHidden && (e.content.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) || (e.tags && e.tags.some(t => t.toLowerCase().includes(debouncedSearchTerm.toLowerCase()))))), [userPublicWhispers, debouncedSearchTerm]);

    const badgeToDisplay = useMemo(() => {
        if (!profileUser?.badges) return null;
        const legacyBadges = ['elder_star', 'giant_star', 'radiant_star', 'ascendant_star', 'proto_star', 'the_spark'];
        return profileUser.badges.find(b => legacyBadges.includes(b));
    }, [profileUser]);

    const canMessage = !isSelf && (
        ((profileUser?.privacySettings?.messagePolicy || 'everyone') === 'everyone') ||
        (((profileUser?.privacySettings?.messagePolicy || 'everyone') === 'connections') && isConnected)
    );

    if (isLoading || !profileUser) return <LoadingSpinner message={t('userProfile.loading')} />;

    const themeClass = profileUser?.profileTheme ? `profile-theme-${profileUser.profileTheme}` : '';

    return (
        <div className={`p-4 sm:p-6 glass-panel max-w-4xl mx-auto text-gray-100 animate-fadeIn ${themeClass}`}>
            <Helmet>
                <title>{profileUser ? `${profileUser.displayName}'s Profile - Whispers of Harmony` : 'User Profile - Whispers of Harmony'}</title>
                <meta name="description" content={profileUser?.bio || "View this user's profile, badges, and moments on Whispers of Harmony."} />
            </Helmet>
            <EditProfileModal 
                isOpen={showEditProfileModal} 
                onClose={() => setShowEditProfileModal(false)} 
                currentUserProfile={profileUser} 
                onSave={handleSaveProfile} 
            />
            {isModalOpen && <AIGeneratedContentModal title={aiModalContent.title} content={aiModalContent.content} onClose={() => setIsModalOpen(false)} LucideIcons={LucideIcons} />}
            
            {profileInsight && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-gray-900 border border-purple-500/50 rounded-xl shadow-2xl max-w-md w-full p-6 relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500"></div>
                        <button onClick={() => setProfileInsight(null)} className="absolute top-4 right-4 text-gray-400 hover:text-white"><LucideIcons.X size={24} /></button>
                        
                        <div className="text-center mb-6">
                            <LucideIcons.Sparkles size={48} className="mx-auto text-purple-400 mb-2" />
                            <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-blue-400 font-sans">The Mirror</h3>
                            <p className="text-sm text-gray-400 mt-1">Your digital reflection revealed.</p>
                        </div>

                        <div className="space-y-6">
                            <div className="text-center">
                                <p className="text-xs text-purple-400 uppercase tracking-widest mb-1">Archetype</p>
                                <h4 className="text-3xl font-bold text-white">{profileInsight.archetype}</h4>
                                <p className="text-gray-300 italic mt-2">"{profileInsight.aura}"</p>
                            </div>

                            <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                                <p className="text-xs text-green-400 uppercase tracking-widest mb-2 font-bold">Strengths</p>
                                <ul className="space-y-2">
                                    {profileInsight.strengths.map((strength, i) => (
                                        <li key={i} className="flex items-start gap-2 text-sm text-gray-200">
                                            <LucideIcons.CheckCircle2 size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                                            <span>{strength}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                                <p className="text-xs text-indigo-400 uppercase tracking-widest mb-2 font-bold">A Gentle Nudge</p>
                                <div className="flex items-start gap-2 text-sm text-gray-300">
                                    <LucideIcons.Sparkles size={16} className="text-indigo-400 mt-0.5 flex-shrink-0" />
                                    <p>{profileInsight.shadow}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {soulBlueprint && <SoulBlueprintDisplay blueprint={soulBlueprint} LucideIcons={LucideIcons} t={t} onClose={() => setSoulBlueprint(null)} />}

            {showBioModal && <BioEnhancerModal suggestions={bioSuggestions} onClose={() => setShowBioModal(false)} onSelect={(bio) => { /* Handle bio selection if needed or handled in modal */ setShowBioModal(false); }} LucideIcons={LucideIcons} t={t} />}
            {statModal.isOpen && <ProfileStatModal title={statModal.title} value={statModal.value} history={statModal.history} onClose={() => setStatModal({ isOpen: false, title: '', value: 0, history: [] })} />}
            {showAuraComparison && <AuraComparisonModal currentUserData={auraComparisonData.currentUserData} targetUserData={auraComparisonData.targetUserData} onClose={() => setShowAuraComparison(false)} />}
            {showBadgeInfoModal && <BadgeInfoModal onClose={() => setShowBadgeInfoModal(false)} />}
            {showCreateContentModal && <CreateContentModal onClose={() => { setShowCreateContentModal(false); setRemixingWhisper(null); setInitialMediaForWhisper(null); }} initialMedia={initialMediaForWhisper} remixingWhisper={remixingWhisper} />}
            {interactionModal.isOpen && <InteractionUsersModal users={interactionModal.users} title={interactionModal.title} onClose={() => setInteractionModal({ isOpen: false, users: [], title: '' })} />}
            {showShowdownModal && (
                <ShowdownModal 
                    onClose={() => setShowShowdownModal(false)}
                    rivalId={profileUser.id}
                    challengeMode={true}
                />
            )}
            {commentingWhisper && (
                <CommentModal 
                    post={commentingWhisper} 
                    onClose={() => setCommentingWhisper(null)} 
                />
            )}
            {aiToolsWhisper && (
                <div 
                    className="absolute z-50"
                    style={{ top: aiToolsPosition.top, left: aiToolsPosition.left }}
                >
                    {/* This will be the AiToolsButton component's dropdown content */}
                </div>
            )}

            <PinnedBadges pinnedBadgeIds={pinnedBadgeIds} />

            <div className="profile-header" style={profileUser.headerImageURL ? { backgroundImage: `url(${profileUser.headerImageURL})` } : {}}>
                <div className="relative group inline-block">
                    {/* Living Aura based on login streak */}
                    <div className={`absolute inset-[-4px] rounded-full opacity-70 blur-[6px] animate-pulse transition-all duration-1000 ${(profileUser?.stats?.loginStreak || 0) > 7 ? 'bg-gradient-to-r from-orange-400 via-pink-500 to-purple-500 blur-[8px] opacity-100' : (profileUser?.stats?.loginStreak || 0) > 3 ? 'bg-gradient-to-r from-blue-400 to-indigo-500' : 'bg-transparent'}`}></div>
                    
                    <img 
                        src={profileUser.photoURL || "https://placehold.co/100x100/AEC6CF/FFFFFF?text=U"} 
                        alt={profileUser.displayName} 
                        className={`profile-avatar relative z-10 transition-opacity duration-300 cursor-pointer border-4 ${(profileUser?.stats?.loginStreak || 0) > 7 ? 'border-pink-400' : (profileUser?.stats?.loginStreak || 0) > 3 ? 'border-blue-400' : 'border-gray-700'}`} 
                        onClick={() => profileUser.photoURL && setMediaToView(profileUser.photoURL)}
                    />
                    {profileUser?.proStatus === 'active' && <div className="absolute -top-1 -right-1 p-1.5 bg-purple-600 rounded-full border-2 border-gray-900 z-20" title={t('userProfile.harmonyPro')}><LucideIcons.Crown size={14} className="text-amber-300" /></div>}
                </div>
                
                <h2 className="text-3xl font-bold">{profileUser.displayName}<BadgeIconStrip badges={profileUser.badges} /></h2>

                {humanReadableLocation && <p className="text-sm text-gray-400 mt-1 flex items-center justify-center gap-1"><LucideIcons.MapPin size={14} /> {humanReadableLocation}</p>}

                {badgeToDisplay && badgeConfig[badgeToDisplay] && (
                    <div className="flex items-center gap-2 mt-3 px-3 py-1 bg-gray-900/50 rounded-full border border-gray-600">
                        <LucideIcons.Sparkles size={14} className={badgeConfig[badgeToDisplay].color} />
                        <span className={`font-bold text-sm ${badgeConfig[badgeToDisplay].color}`}>{badgeConfig[badgeToDisplay].name}</span>
                    </div>
                )}

                <p className="text-md text-gray-300 mt-2 max-w-md">"{profileUser.bio || t('userProfile.defaultBio')}"</p>

                <div className="profile-stats flex-wrap">
                    <button onClick={() => openStatModal(t('userProfile.stats.reputation'), Math.round(profileUser.reputationScore) || 0, 'reputation')} className="profile-stat-item"><span className="stat-value">{Math.round(profileUser.reputationScore) || 0}</span><span>{t('userProfile.stats.reputation')}</span></button>
                    <button onClick={() => openStatModal(t('userProfile.stats.influence'), Math.round(profileUser.influenceScore) || 0, 'influence')} className="profile-stat-item"><span className="stat-value">{Math.round(profileUser.influenceScore) || 0}</span><span>{t('userProfile.stats.influence')}</span></button>
                    <button onClick={() => openStatModal(t('userProfile.stats.whispers'), userPublicWhispers.length, 'whispers')} className="profile-stat-item"><span className="stat-value">{userPublicWhispers.length}</span><span>{t('userProfile.stats.whispers')}</span></button>
                    <button onClick={() => openStatModal(t('userProfile.stats.connections'), isSelf ? userConnections.length : profileUser.followerCount || 0, 'connections')} className="profile-stat-item"><span className="stat-value">{isSelf ? userConnections.length : profileUser.followerCount || 0}</span><span>{t('userProfile.stats.connections')}</span></button>
                </div>
                <div className="w-full max-w-md mx-auto mt-4 space-y-4">
                    {nextRepLevel && <ProgressBar value={profileUser.reputationScore || 0} max={nextRepLevel.threshold} label={`${t('userProfile.to')} ${nextRepLevel.name}`} colorClass="bg-green-500" />}
                    {nextInfLevel && <ProgressBar value={profileUser.influenceScore || 0} max={nextInfLevel.threshold} label={`${t('userProfile.to')} ${nextInfLevel.name}`} colorClass="bg-purple-500" />}
                </div>
            </div>

            {pinnedWhisper && (
                <div className="my-6 p-4 bg-yellow-800/20 rounded-lg border border-yellow-500/30">
                    <h3 className="text-lg font-bold text-yellow-200 font-sans mb-2 flex items-center gap-2">
                        <LucideIcons.Pin size={16} /> {t('userProfile.pinnedWhisper')}
                    </h3>
                    <div className="bg-white/5 border border-white/10 p-4 rounded-xl">
                        <p className="text-xs text-gray-400 mb-2">{pinnedWhisper.timestamp?.toDate().toLocaleString()}</p>
                        <p className="italic text-gray-200">"{pinnedWhisper.content}"</p>
                        {pinnedWhisper.mediaUrls && pinnedWhisper.mediaUrls.length > 0 && (
                            <img src={pinnedWhisper.mediaUrls[0]} alt="Pinned media" className="w-full h-auto rounded-lg mt-2" />
                        )}
                    </div>
                </div>
            )}

            <div className="flex flex-wrap justify-center gap-2 mb-6 relative">
                {isSelf ? (
                    <>
                        <button onClick={() => setShowEditProfileModal(true)} className="small-action-button bg-blue-600 hover:bg-blue-700">
                            <LucideIcons.Edit3 size={14} className="mr-2" />
                            {t('userProfile.actions.editProfile')}
                        </button>
                        
                        <div className="relative inline-block text-left">
                            <button 
                                onClick={(e) => { e.stopPropagation(); setShowActionsDropdown(!showActionsDropdown); }} 
                                className="small-action-button bg-gray-800 hover:bg-gray-700 text-white flex items-center gap-1"
                            >
                                <LucideIcons.Sparkles size={14} />
                                <span>AI Actions</span>
                                <LucideIcons.ChevronDown size={14} />
                            </button>
                            
                            {showActionsDropdown && (
                                <div className="absolute right-0 mt-2 w-56 rounded-xl shadow-lg bg-gray-900/95 border border-white/10 backdrop-blur-md z-50 py-1 origin-top-right focus:outline-none animate-in fade-in slide-in-from-top-2 duration-200">
                                    <button 
                                        onClick={() => {
                                            setShowActionsDropdown(false);
                                            showConfirmation({
                                                message: t('userProfile.actions.getSoulBlueprintConfirmation', { cost: 50 }),
                                                onConfirm: handleGetSoulBlueprint
                                            });
                                        }}
                                        disabled={isLoadingBlueprint}
                                        className="w-full text-left px-4 py-2.5 text-sm text-gray-200 hover:bg-white/10 hover:text-white transition-all flex items-center gap-2"
                                    >
                                        {isLoadingBlueprint ? <div className="action-spinner" /> : <><LucideIcons.Fingerprint size={14} />{t('userProfile.actions.analyzePersonality')}</>}
                                    </button>
                                    
                                    <button 
                                        onClick={() => {
                                            setShowActionsDropdown(false);
                                            showConfirmation({
                                                message: t('userProfile.actions.getAiPromptConfirmation', { cost: 30 }),
                                                onConfirm: handleGetProfileInsight
                                            });
                                        }}
                                        disabled={isLoadingInsight}
                                        className="w-full text-left px-4 py-2.5 text-sm text-gray-200 hover:bg-white/10 hover:text-white transition-all flex items-center gap-2"
                                    >
                                        {isLoadingInsight ? <div className="action-spinner" /> : <><LucideIcons.Sparkles size={14} />{t('userProfile.actions.aiPrompt')}</>}
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        {canMessage && (
                            <button onClick={() => onMessageUser(profileUser.id, profileUser.displayName)} className="small-action-button bg-blue-600 hover:bg-blue-700">
                                <LucideIcons.MessageSquare size={14} className="mr-1" />
                                {t('userProfile.actions.message')}
                            </button>
                        )}
                        
                        <button onClick={() => onToggleConnection(profileUser.id, isConnected)} className={`small-action-button ${isConnected ? 'bg-gray-600 hover:bg-gray-700' : 'bg-purple-600 hover:bg-purple-700'}`}>
                            {isConnected ? t('userProfile.actions.disconnect') : t('userProfile.actions.connect')}
                        </button>
                        
                        <div className="relative inline-block text-left">
                            <button 
                                onClick={(e) => { e.stopPropagation(); setShowActionsDropdown(!showActionsDropdown); }} 
                                className="small-action-button bg-gray-800 hover:bg-gray-700 text-white flex items-center gap-1"
                            >
                                <LucideIcons.MoreHorizontal size={14} />
                                <span>More</span>
                                <LucideIcons.ChevronDown size={14} />
                            </button>
                            
                            {showActionsDropdown && (
                                <div className="absolute right-0 mt-2 w-56 rounded-xl shadow-lg bg-gray-900/95 border border-white/10 backdrop-blur-md z-50 py-1 origin-top-right focus:outline-none animate-in fade-in slide-in-from-top-2 duration-200">
                                    <button 
                                        onClick={() => { setShowActionsDropdown(false); setShowShowdownModal(true); }}
                                        className="w-full text-left px-4 py-2.5 text-sm text-gray-200 hover:bg-white/10 hover:text-white transition-all flex items-center gap-2"
                                    >
                                        <LucideIcons.Swords size={14} className="text-red-400" />
                                        <span>{t('userProfile.actions.challenge') || "Challenge"}</span>
                                    </button>
                                    
                                    <button 
                                        onClick={() => { setShowActionsDropdown(false); handleToggleInnerCircle(); }}
                                        className="w-full text-left px-4 py-2.5 text-sm text-gray-200 hover:bg-white/10 hover:text-white transition-all flex items-center gap-2"
                                    >
                                        {isInnerCircle ? <LucideIcons.HeartOff size={14} className="text-pink-400" /> : <LucideIcons.Heart size={14} className="text-pink-400" />}
                                        <span>{isInnerCircle ? (t('userProfile.actions.removeFromInnerCircle') || "Remove from Inner Circle") : (t('userProfile.actions.addToInnerCircle') || "Add to Inner Circle")}</span>
                                    </button>
                                    
                                    <button 
                                        onClick={() => {
                                            setShowActionsDropdown(false);
                                            showConfirmation({
                                                message: t('userProfile.actions.getConversationStartersConfirmation', { cost: TOKEN_COSTS.CONVERSATION_STARTER }),
                                                onConfirm: handleGetConversationStarters
                                            });
                                        }}
                                        disabled={isLoadingStarters}
                                        className="w-full text-left px-4 py-2.5 text-sm text-gray-200 hover:bg-white/10 hover:text-white transition-all flex items-center gap-2"
                                    >
                                        {isLoadingStarters ? <div className="action-spinner" /> : <><LucideIcons.MessageSquarePlus size={14} />{t('userProfile.actions.suggestMessage')}</>}
                                    </button>
                                    
                                    <button 
                                        onClick={() => {
                                            setShowActionsDropdown(false);
                                            showConfirmation({
                                                message: t('userProfile.actions.compareAurasConfirmation', { cost: 30 }),
                                                onConfirm: handleGetSnapshot
                                            });
                                        }}
                                        disabled={isLoadingSnapshot}
                                        className="w-full text-left px-4 py-2.5 text-sm text-gray-200 hover:bg-white/10 hover:text-white transition-all flex items-center gap-2"
                                    >
                                        {isLoadingSnapshot ? <div className="action-spinner" /> : <><LucideIcons.BrainCircuit size={14} />{t('userProfile.actions.compareAuras')}</>}
                                    </button>
                                    
                                    {canModerate && (
                                        <button 
                                            onClick={() => { setShowActionsDropdown(false); handleBanUser(); }}
                                            className="w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-red-950/30 hover:text-red-300 transition-all flex items-center gap-2"
                                        >
                                            <LucideIcons.Ban size={14} />
                                            <span>{t('userProfile.actions.banUser') || "Ban User"}</span>
                                        </button>
                                    )}
                                    
                                    <div className="border-t border-white/5 my-1"></div>
                                    
                                    <button 
                                        onClick={() => { setShowActionsDropdown(false); handleBlockUser(); }}
                                        className="w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-red-950/30 hover:text-red-300 transition-all flex items-center gap-2"
                                    >
                                        <LucideIcons.ShieldAlert size={14} />
                                        <span>Block User</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            {!isSelf && <MutualConnections mutuals={mutualConnections} onUserSelect={handleUserSelect} />}

            {personalitySnapshot && <PersonalitySnapshotDisplay snapshot={personalitySnapshot} LucideIcons={LucideIcons} t={t} />}

            <div className="w-full">
                <div className="flex justify-center items-center gap-2 border-b border-gray-700">
                    <div className="profile-tabs">
                        <button onClick={() => { setActiveTab('content'); setActiveSubTab('published'); }} className={`profile-tab-button ${activeTab === 'content' ? 'active' : ''}`} style={activeTab === 'content' ? { borderColor: profileUser.accentColor, color: profileUser.accentColor } : {}}>{t('userProfile.tabs.content')}</button>
                        <button onClick={() => { setActiveTab('insights'); setActiveSubTab('journey'); }} className={`profile-tab-button ${activeTab === 'insights' ? 'active' : ''}`} style={activeTab === 'insights' ? { borderColor: profileUser.accentColor, color: profileUser.accentColor } : {}}>{t('userProfile.tabs.insights')}</button>
                        <button onClick={() => { setActiveTab('community'); setActiveSubTab('nexuses'); }} className={`profile-tab-button ${activeTab === 'community' ? 'active' : ''}`} style={activeTab === 'community' ? { borderColor: profileUser.accentColor, color: profileUser.accentColor } : {}}>{t('userProfile.tabs.community')}</button>
                        <button onClick={() => { setActiveTab('constellation'); setActiveSubTab(''); }} className={`profile-tab-button ${activeTab === 'constellation' ? 'active' : ''}`} style={activeTab === 'constellation' ? { borderColor: profileUser.accentColor, color: profileUser.accentColor } : {}}>{t('userProfile.tabs.constellation')}</button>
                        {(isSelf || currentUserProfile.role === 'owner') && <button onClick={() => { setActiveTab('settings'); setActiveSubTab(isSelf ? 'themes' : 'admin'); }} className={`profile-tab-button ${activeTab === 'settings' ? 'active' : ''}`} style={activeTab === 'settings' ? { borderColor: profileUser.accentColor, color: profileUser.accentColor } : {}}>{t('userProfile.tabs.settings')}</button>}
                    </div>
                    <button onClick={() => setShowBadgeInfoModal(true)} className="p-2 text-gray-400 hover:text-white rounded-full hover:bg-gray-700/50"><LucideIcons.Info size={18} /></button>
                </div>
                <div className="flex justify-center mt-4">
                    {activeTab === 'content' && (
                        <div className="flex rounded-full bg-gray-800 p-1">
                            <button onClick={() => setActiveSubTab('published')} className={`px-4 py-2 text-sm font-semibold rounded-full ${activeSubTab === 'published' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}>{t('userProfile.tabs.whispers')}</button>
                            <button onClick={() => setActiveSubTab('media')} className={`px-4 py-2 text-sm font-semibold rounded-full ${activeSubTab === 'media' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}>{t('userProfile.tabs.media')}</button>
                            <button onClick={() => setActiveSubTab('highlights')} className={`px-4 py-2 text-sm font-semibold rounded-full ${activeSubTab === 'highlights' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}>{t('userProfile.tabs.highlights')}</button>
                        </div>
                    )}
                    {activeTab === 'insights' && (
                        <div className="flex rounded-full bg-gray-800 p-1">
                            <button onClick={() => setActiveSubTab('journey')} className={`px-4 py-2 text-sm font-semibold rounded-full ${activeSubTab === 'journey' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}>{t('userProfile.tabs.journey')}</button>
                            <button onClick={() => setActiveSubTab('thematicCloud')} className={`px-4 py-2 text-sm font-semibold rounded-full ${activeSubTab === 'thematicCloud' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}>{t('userProfile.tabs.thematicCloud')}</button>
                        </div>
                    )}
                    {activeTab === 'community' && (
                        <div className="flex rounded-full bg-gray-800 p-1">
                            <button onClick={() => setActiveSubTab('nexuses')} className={`px-4 py-2 text-sm font-semibold rounded-full ${activeSubTab === 'nexuses' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}>{t('userProfile.tabs.nexuses')}</button>
                            <button onClick={() => setActiveSubTab('badges')} className={`px-4 py-2 text-sm font-semibold rounded-full ${activeSubTab === 'badges' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}>{t('userProfile.tabs.badges')}</button>
                        </div>
                    )}
                    {activeTab === 'settings' && (isSelf || currentUserProfile.role === 'owner') && (
                        <div className="flex rounded-full bg-gray-800 p-1">
                            {isSelf && <button onClick={() => setActiveSubTab('themes')} className={`px-4 py-2 text-sm font-semibold rounded-full ${activeSubTab === 'themes' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}>{t('userProfile.tabs.themes')}</button>}
                            {currentUserProfile.role === 'owner' && <button onClick={() => setActiveSubTab('admin')} className={`px-4 py-2 text-sm font-semibold rounded-full ${activeSubTab === 'admin' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}>{t('userProfile.tabs.admin')}</button>}
                        </div>
                    )}
                </div>

                <div className="mt-4">
                    {canViewProfile ? (
                        <>
                            {activeTab === 'content' && (
                                <>
                                    {activeSubTab === 'published' && (isSelf 
                                        ? <MyWhispersTab whispers={filteredWhispers} loadingStates={loadingStates} currentUserProfile={currentUserProfile} onSpotlight={handleSetSpotlight} onAiAction={handleWhisperAiAction} onDelete={(id) => showConfirmation({ message: t('userProfile.myWhispers.deleteConfirmation'), onConfirm: () => handleDeleteWhisper(id) })} showConfirmation={showConfirmation} onPin={handlePinWhisper} t={t} onRemix={onRemix} onBookmark={handleBookmark} onShowInteractions={handleShowInteractions} onComment={setCommentingWhisper} onAiToolsClick={handleAiToolsClick} setIsAiMenuOpen={setIsAiMenuOpen} /> 
                                        : <ProfileWhispersTab whispers={filteredWhispers} t={t} profileUser={profileUser} onRemix={onRemix} onBookmark={handleBookmark} onShowInteractions={handleShowInteractions} onComment={setCommentingWhisper} onAiToolsClick={handleAiToolsClick} setIsAiMenuOpen={setIsAiMenuOpen} />
                                    )}
                                    {activeSubTab === 'media' && (isLoadingMedia 
                                        ? <LoadingSpinner /> 
                                        : <ProfileMediaTab whispers={userMediaItems} onDelete={handleDeleteWhisper} showConfirmation={showConfirmation} isSelf={isSelf} t={t} onWhisperFromMedia={onWhisperFromMedia} />
                                    )}
                                    {activeSubTab === 'highlights' && (isLoadingHighlights 
                                        ? <LoadingSpinner /> 
                                        : <ProfileHighlightsTab highlights={userHighlights} t={t} />
                                    )}
                                </>
                            )}
                            {activeTab === 'insights' && (
                                <>
                                    {activeSubTab === 'journey' && <JourneyTab whispers={userPublicWhispers} t={t} />}
                                    {activeSubTab === 'thematicCloud' && <ThematicCloudDisplay cloud={thematicCloud} LucideIcons={LucideIcons} onThemeClick={(theme) => console.log(theme)} t={t} />}
                                </>
                            )}
                            {activeTab === 'community' && (
                                <>
                                    {activeSubTab === 'nexuses' && <ProfileNexusesTab nexuses={userNexuses} t={t} />}
                                    {activeSubTab === 'badges' && <ProfileBadgesTab badgeIds={profileUser.badges} isSelf={isSelf} pinnedBadgeIds={pinnedBadgeIds} onPinBadge={handlePinBadge} t={t} badgeConfig={badgeConfig} />}
                                </>
                            )}
                            {activeTab === 'constellation' && (
                                <ProfileConstellationTab 
                                    targetUserId={profileUserId} 
                                    db={db} 
                                    appId={appId} 
                                    userProfiles={userProfiles} 
                                />
                            )}
                            {activeTab === 'settings' && (isSelf || currentUserProfile.role === 'owner') && (
                                <>
                                    {activeSubTab === 'themes' && isSelf && <ThemesTab profileUser={profileUser} isSelf={isSelf} t={t} />}
                                    {activeSubTab === 'admin' && currentUserProfile.role === 'owner' && (
                                        <>
                                            <AdminBadgesTab 
                                                allBadges={badgeConfig}
                                                userBadges={profileUser.badges || []}
                                                onToggle={handleToggleBadge}
                                                t={t}
                                            />
                                            <AdminThemesTab 
                                                allThemes={getProfileThemes(t)}
                                                userThemes={profileUser.ownedThemes || []}
                                                onToggle={handleToggleTheme}
                                                t={t}
                                            />
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    ) : (
                        <div className="text-center text-gray-300 p-8 bg-white/5 border border-white/10 rounded-xl mt-4">
                            <LucideIcons.Lock size={48} className="mx-auto mb-4 text-purple-400" />
                            <h3 className="text-xl font-bold">{t('userProfile.privateProfile.title')}</h3>
                            <p className="mt-2">{t('userProfile.privateProfile.description', { displayName: profileUser.displayName })}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default UserProfile;

const ProfileHighlightsTab = ({ highlights, t }) => {
    const { LucideIcons } = useAppContext();

    if (highlights.length === 0) {
        return (
            <div className="text-center text-gray-300 p-8 bg-white/5 border border-white/10 rounded-xl">
                <LucideIcons.Sparkles size={48} className="mx-auto mb-4 text-yellow-400" />
                <h3 className="text-xl font-bold">{t('userProfile.highlights.emptyTitle')}</h3>
                <p className="mt-2">{t('userProfile.highlights.emptyDescription')}</p>
            </div>
        );
    }

    return (
        <Masonry
            breakpointCols={{ default: 4, 1100: 3, 700: 2, 500: 1 }}
            className="my-masonry-grid"
            columnClassName="my-masonry-grid_column"
        >
            {highlights.map(highlight => (
                <div key={highlight.id} className="relative rounded-lg overflow-hidden shadow-lg group">
                    {highlight.type === 'image' && <img src={highlight.contentURL} alt="Highlight" className="w-full h-auto object-cover" />}
                    {highlight.type === 'video' && <video src={highlight.contentURL} muted loop className="w-full h-auto object-cover" />}
                    {highlight.type === 'text' && (
                        <div className="w-full h-48 flex items-center justify-center p-4" style={{ backgroundColor: highlight.backgroundColor || '#000' }}>
                            <p className="text-white text-lg font-bold text-center line-clamp-4">{highlight.text}</p>
                        </div>
                    )}
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <p className="text-white text-center p-4">{highlight.text ? highlight.text.substring(0, 100) + '...' : ''}</p>
                    </div>
                </div>
            ))}
        </Masonry>
    );
};